import { PresentationFitIssue, PresentationFitStatus, PresentationLayout } from '@/api/presentations/presentations'
import type {
  PresentationFitIssue as PresentationFitIssueType,
  PresentationFitPolicy,
  PresentationFitResult,
  PresentationSlide
} from '@/api/presentations/presentations'

type VisibleSlide = Pick<PresentationSlide, 'title' | 'keyMessage' | 'contentPoints' | 'layout'>

function wrappedLines(value: string, charsPerLine: number) {
  const trimmed = value.trim()
  if (!trimmed) return 0
  return trimmed.split('\n').reduce((total, line) => {
    const count = Array.from(line.trim()).length
    return total + (count === 0 ? 1 : Math.ceil(count / charsPerLine))
  }, 0)
}

function appendIssue(issues: Array<PresentationFitIssueType>, issue: PresentationFitIssueType) {
  if (!issues.includes(issue)) issues.push(issue)
}

function bodyFit(
  keyMessage: string,
  points: Array<string>,
  limits: PresentationFitPolicy['layouts']['title_and_content']
) {
  const issues: Array<PresentationFitIssueType> = []
  const keyLines = wrappedLines(keyMessage, limits.bodyCharsPerLine)
  let used = keyLines
  if (keyLines > limits.maxKeyMessageLines) appendIssue(issues, PresentationFitIssue.KeyMessageTooLong)
  if (keyLines > 0 && points.length > 0) used += 1
  if (points.length > limits.maxPoints) appendIssue(issues, PresentationFitIssue.TooManyPoints)
  for (const point of points) {
    const pointLines = wrappedLines(point, limits.bodyCharsPerLine)
    used += pointLines
    if (pointLines > limits.maxPointLines) appendIssue(issues, PresentationFitIssue.PointTooLong)
  }
  if (used > limits.maxBodyLines) appendIssue(issues, PresentationFitIssue.BodyOverflow)
  return { used, issues }
}

export function evaluatePresentationFit(slide: VisibleSlide, policy: PresentationFitPolicy): PresentationFitResult {
  const limits = policy.layouts[slide.layout]
  const titleLines = wrappedLines(slide.title, limits.titleCharsPerLine)
  const issues: Array<PresentationFitIssueType> = []
  if (titleLines > limits.maxTitleLines) appendIssue(issues, PresentationFitIssue.TitleTooLong)

  let bodyUsed: number
  if (slide.layout === PresentationLayout.TwoColumn) {
    const midpoint = Math.ceil(slide.contentPoints.length / 2)
    const left = bodyFit(slide.keyMessage, slide.contentPoints.slice(0, midpoint), limits)
    const right = bodyFit('', slide.contentPoints.slice(midpoint), limits)
    if (left.used > limits.maxBodyLines) appendIssue(left.issues, PresentationFitIssue.LeftColumnOverflow)
    if (right.used > limits.maxBodyLines) appendIssue(right.issues, PresentationFitIssue.RightColumnOverflow)
    for (const issue of [...left.issues, ...right.issues]) appendIssue(issues, issue)
    bodyUsed = Math.max(left.used, right.used)
  } else {
    const body = bodyFit(slide.keyMessage, slide.contentPoints, limits)
    for (const issue of body.issues) appendIssue(issues, issue)
    bodyUsed = body.used
  }

  const usedUnits = titleLines + bodyUsed
  const capacityUnits = limits.maxTitleLines + limits.maxBodyLines
  const status =
    issues.length > 0 || usedUnits > capacityUnits
      ? PresentationFitStatus.Overflow
      : usedUnits * 100 > capacityUnits * policy.comfortablePercent
        ? PresentationFitStatus.Tight
        : PresentationFitStatus.Comfortable
  return { status, usedUnits, capacityUnits, issues }
}

export const presentationFitLabel = {
  [PresentationFitStatus.Comfortable]: 'Fits',
  [PresentationFitStatus.Tight]: 'Getting tight',
  [PresentationFitStatus.Overflow]: 'Too much content'
} as const

export const presentationFitIssueLabels: Record<PresentationFitIssueType, string> = {
  [PresentationFitIssue.TitleTooLong]: 'Title too long',
  [PresentationFitIssue.KeyMessageTooLong]: 'Key message too long',
  [PresentationFitIssue.TooManyPoints]: 'Too many points',
  [PresentationFitIssue.PointTooLong]: 'Point too long',
  [PresentationFitIssue.BodyOverflow]: 'Body overflow',
  [PresentationFitIssue.LeftColumnOverflow]: 'Left column overflow',
  [PresentationFitIssue.RightColumnOverflow]: 'Right column overflow'
}

export const presentationFitIssueMessage: Record<PresentationFitIssueType, string> = {
  [PresentationFitIssue.TitleTooLong]: 'Shorten the slide title.',
  [PresentationFitIssue.KeyMessageTooLong]: 'Shorten the key message.',
  [PresentationFitIssue.TooManyPoints]: 'Remove or combine content points.',
  [PresentationFitIssue.PointTooLong]: 'Shorten the longest content point.',
  [PresentationFitIssue.BodyOverflow]: 'Condense the visible slide copy.',
  [PresentationFitIssue.LeftColumnOverflow]: 'Condense the left column.',
  [PresentationFitIssue.RightColumnOverflow]: 'Condense the right column.'
}
